import { useClientTranslations } from "../../lib/i18n/client";
import type { Journey } from "../../types/journey";
import styles from "./JourneyHeader.module.css";
import { JourneyProgressCircle } from "./JourneyProgressCircle";

interface JourneyHeaderProps {
	journey: Journey;
	/** 0-based index of step where status === "current"; -1 if none. */
	currentStepIndex: number;
	totalSteps: number;
	/** D2C has no progress UI — hide the step progress circle when true. */
	hideProgress?: boolean;
}

export function JourneyHeader({
	journey,
	currentStepIndex,
	totalSteps,
	hideProgress = false,
}: JourneyHeaderProps) {
	const { t } = useClientTranslations("journey");

	// Display "N/M" where N is 1-based and clamped >= 0
	const displayCurrent = currentStepIndex >= 0 ? currentStepIndex + 1 : 0;

	const treatmentLabel = t(`treatment.${journey.type}`, {
		defaultValue: journey.name,
	});

	// Status line shows only the static phase label ("Active Treatments"). The
	// per-step "| Day N of ~M" suffix (currentStep.highlight.currentDayLabel) is
	// hidden for now — the cycle-day estimate is unreliable. To restore, append
	// `| ${journey.steps[currentStepIndex]?.highlight?.currentDayLabel}` when set.
	const statusText = t("statusFallback");

	return (
		<div className={styles.header}>
			{!hideProgress && (
				<JourneyProgressCircle current={displayCurrent} total={totalSteps} />
			)}
			<div className={styles.titleBlock}>
				<h1 className={styles.title}>{t("title")}</h1>
				<p className={styles.subLine}>{treatmentLabel}</p>
				<p className={styles.subLine}>{statusText}</p>
			</div>
		</div>
	);
}
